<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useDateTime, type OAudioController } from '@ai/ui-components'
import type { TableState } from '@/composables/configurator/useConfiguratorTable'
import type { ColdMonitoringResponseCall, VaLanguage } from 'shared'
import { useLanguages } from '@/composables/translations/useLanguages'
import { STORAGE_KEYS } from '@/enums/StorageEnum'
import StopIcon from '@/assets/img/svg/icons/stop_solid.svg?component'
import type { PaginationState } from '@ai/ui-components'
import { usePhoneFormatter } from '@/composables/formatting/usePhoneFormatter'
import { ActionButton, LIST_ACTION_ICONS } from '@/components/BusinessLogic/TableActions'
import { useCopyCallLink } from '@/composables/ColdMonitoring/useCopyCallLink'
import { DEFAULT_PAGE_SIZE } from '@/composables/ColdMonitoring/useColdMonitoringCoordinator'
import type { RouteLocationRaw } from 'vue-router'
import { isNotBlank } from '@/utils/String'

interface Props {
  calls: ColdMonitoringResponseCall[]
  pagination?: PaginationState
  loading?: boolean
  audioController: OAudioController
  detailsTo?: (id: string) => RouteLocationRaw
}

const props = defineProps<Props>()
const emit = defineEmits<{
  (e: 'update:currentPage' | 'update:itemsPerPage', value: number): void
  (e: 'request-audio' | 'download-call' | 'report-call', id: string): void
}>()
const { t, locale } = useI18n({ useScope: 'global' })
const { formatDateTime, formatTimerDuration } = useDateTime()
const { languageLabel } = useLanguages()
const formatLanguage = (language: VaLanguage) => languageLabel(locale.value, language)

const rows = computed(() =>
  props.calls.map((item) => {
    const info = item.callInfo
    const durationSec = Math.max(0, Math.floor((info.endTime - info.startTime) / 1000))
    const agent = info.agent as typeof info.agent | undefined
    const agentName = isNotBlank(agent?.name)
      ? agent.name
      : info.isLiveTest
        ? t('cold_monitoring.unsaved')
        : t('deleted_agent')
    const languageName = formatLanguage(info.language)
    const disconnectedBy = info.disconnectedBy ? t(info.disconnectedBy) : '—'

    return {
      ...info,
      // Keep sortable columns as primitive values for OTable comparator.
      agent: agentName,
      language: languageName,
      disconnectedBy,
      duration: durationSec,
      durationSec,
    }
  }),
)

const paginationBinding = computed(() => ({
  currentPage: props.pagination?.currentPage ?? 1,
  itemsPerPage: props.pagination?.itemsPerPage ?? DEFAULT_PAGE_SIZE,
  totalItems: props.pagination?.totalItems ?? rows.value.length,
  hasMore: props.pagination?.hasMore ?? false,
}))

const currentPageModel = computed({
  get: () => paginationBinding.value.currentPage,
  set: (value: number) => {
    emit('update:currentPage', value)
  },
})

const itemsPerPageModel = computed({
  get: () => paginationBinding.value.itemsPerPage,
  set: (value: number) => {
    emit('update:itemsPerPage', value)
  },
})

const columns = computed(() => [
  { key: 'startTime', label: t('call_start_time') },
  { key: 'agent', label: t('agent') },
  { key: 'duration', label: t('duration') },
  { key: 'language', label: t('language') },
  { key: 'conversationOutput', label: t('conversation_output') },
  { key: 'disconnectedBy', label: t('disconnected_by') },
  { key: 'callerNumber', label: t('caller_number') },
  { key: 'id', label: t('call_id') },
  { key: 'summary', label: t('call_summary') },
])

const PINNED_COLUMN = 'agent'
const allColumnKeys = computed(() => columns.value.map((c) => c.key))
const columnsOptions = computed(() =>
  columns.value.map((c) => ({ label: c.label, value: c.key, disabled: c.key === PINNED_COLUMN })),
)

const selectedVisibleColumns = ref<string[]>([PINNED_COLUMN])
const hasSelectedExtraColumns = computed(() =>
  selectedVisibleColumns.value.some((k) => k !== PINNED_COLUMN),
)

const tableState = ref<TableState>()

const normalizeVisibleKeys = (allKeys: string[], next?: string[] | null): string[] => {
  const set = new Set(Array.isArray(next) ? next : [])
  set.add(PINNED_COLUMN)
  return allKeys.filter((k) => set.has(k))
}

const tableStateModel = computed<TableState | undefined>({
  get: () => tableState.value,
  set: (next) => {
    if (!next) {
      tableState.value = undefined
      return
    }
    const normalized = normalizeVisibleKeys(allColumnKeys.value, next.visibleColumns)
    tableState.value = { ...next, visibleColumns: normalized }
    if (JSON.stringify(selectedVisibleColumns.value) !== JSON.stringify(normalized)) {
      selectedVisibleColumns.value = normalized
    }
  },
})

watch(selectedVisibleColumns, (cols) => {
  const normalized = normalizeVisibleKeys(allColumnKeys.value, cols)
  if (JSON.stringify(tableStateModel.value?.visibleColumns) !== JSON.stringify(normalized)) {
    tableStateModel.value = {
      visibleColumns: normalized,
      columnWidths: tableStateModel.value?.columnWidths ?? {},
    }
  }
})

const listeningId = computed(() => props.audioController.state.value.activeTrack?.id ?? null)
const isListening = (id: string) =>
  listeningId.value === id && props.audioController.state.value.status === 'playing'
const { formatPhone } = usePhoneFormatter()
const { getCopyLinkAction } = useCopyCallLink()
const detailsTo = (id: string) => props.detailsTo?.(id) ?? { query: { callId: id } }

const getRowActions = (item: Pick<ColdMonitoringResponseCall['callInfo'], 'id' | 'isLiveTest'>) => [
  {
    label: t('listen'),
    value: 'listen' as const,
    icon: LIST_ACTION_ICONS.listen,
    disabled: item.isLiveTest,
    handler: () => {
      emit('request-audio', item.id)
    },
    dataTestid: 'action-listen',
  },
  {
    label: t('view_details'),
    value: 'viewDetails' as const,
    icon: LIST_ACTION_ICONS.details,
    to: detailsTo(item.id),
    dataTestid: 'action-view-details',
  },
  getCopyLinkAction(item.id),
  {
    label: t('download'),
    value: 'download' as const,
    icon: LIST_ACTION_ICONS.download,
    handler: () => {
      emit('download-call', item.id)
    },
    dataTestid: 'action-download',
  },
  {
    label: t('cold_monitoring.report_interaction.title'),
    value: 'report' as const,
    icon: LIST_ACTION_ICONS.report,
    handler: () => {
      emit('report-call', item.id)
    },
    dataTestid: 'action-report',
  },
]
</script>

<template>
  <OTable
    id="cold_calls"
    v-model="rows"
    :columns="columns"
    sortable
    resizable
    column-resize-mode="indicator"
    with-pagination
    :loading="props.loading"
    :pagination="paginationBinding"
    :items-per-page-options="[25, 50, 100, 250]"
    v-model:currentPage="currentPageModel"
    v-model:itemsPerPage="itemsPerPageModel"
    v-model:tableState="tableStateModel"
    :state-persistence-key="STORAGE_KEYS.TABLE_STATE_COLD_MONITORING"
  >
    <template #filterActions>
      <div class="flex justify-between items-center w-full">
        <h2 class="typography-heading-2 text-primary-brand mt-1.5">{{ t('call_history') }}</h2>
        <ODropdown
          v-model="selectedVisibleColumns"
          :options="columnsOptions"
          multi-select
          filter
          class="!w-auto"
        >
          <template #icon>
            <UiIcon
              :color="hasSelectedExtraColumns ? 'text-primary' : 'text-color-1'"
              name="column"
              variant="outline"
            />
          </template>
        </ODropdown>
      </div>
    </template>
    <template #callerNumber="{ item }">
      <div v-mp-no-track>
        <template v-if="item.isLiveTest">
          <span class="typography-body-s-medium text-secondary-brand">{{
            t('cold_monitoring.live_test_caller')
          }}</span>
        </template>
        <OOverflowTooltip v-else :content="formatPhone(item.callerNumber)">
          {{ formatPhone(item.callerNumber) }}
        </OOverflowTooltip>
      </div>
    </template>

    <template #startTime="{ item }">
      <OOverflowTooltip
        :content="formatDateTime({ format: 'dateTime', value: item.startTime, fallback: '-' })"
      >
        {{ formatDateTime({ format: 'dateTime', value: item.startTime, fallback: '-' }) }}
      </OOverflowTooltip>
    </template>

    <template #endTime="{ item }">
      <OOverflowTooltip
        :content="formatDateTime({ format: 'dateTime', value: item.endTime, fallback: '-' })"
      >
        {{ formatDateTime({ format: 'dateTime', value: item.endTime, fallback: '-' }) }}
      </OOverflowTooltip>
    </template>

    <template #duration="{ item }">
      <OOverflowTooltip
        :content="
          formatTimerDuration({ value: item.durationSec, alwaysIncludeHours: true, fallback: '-' })
        "
      >
        {{
          formatTimerDuration({ value: item.durationSec, alwaysIncludeHours: true, fallback: '-' })
        }}
      </OOverflowTooltip>
    </template>

    <template #summary="{ item }">
      <div class="-mx-4 -my-3">
        <OOverflowTooltip :content="item.summary ?? ''" :enabled="Boolean(item.summary)" :lines="2">
          {{ item.summary || '-' }}
        </OOverflowTooltip>
      </div>
    </template>

    <template #actions="{ item }">
      <ActionButton
        :tooltip="isListening(item.id) ? t('stop_listening_call') : t('listen')"
        icon="listen"
        :icon-component="isListening(item.id) ? StopIcon : null"
        :disabled="item.isLiveTest"
        data-testid="row-action-listen"
        @click="emit('request-audio', item.id)"
      />

      <ActionButton
        :tooltip="t('view_details')"
        icon="details"
        data-testid="row-action-details"
        :to="detailsTo(item.id)"
      />

      <OListActions small :actions="getRowActions(item)" data-testid="row-action-more" />
    </template>
  </OTable>
</template>
